import { Button, InplaceEdit, LazyLoader } from '@bp/ui-components';
import { type FragmentType, getFragmentData, graphql } from 'client/gql';
import dayjs from 'dayjs';
import { Form, Formik } from 'formik';
import { usePermissionChecker } from 'hooks/usePermissionChecker';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { type CombinedError, useMutation } from 'urql';
import { backendApi } from 'utils/backendApi';
import { useWindowDimensions } from 'utils/dimensions';
import type { BpCreateWorkMaterialsInGroupWorkMaterialsFormMutation } from '../../client/gql/graphql';
import { useAuthClaims } from '../../hooks/useAuthClaims';
import { connectByUuid, connectDay, fileEntriesConnect } from '../../utils/connectLib';
import { NotificationReason } from '../../utils/matrixClient';
import { showErrorToast } from '../../utils/showErrorToast';
import { showSuccessToast } from '../../utils/showSuccessToast';
import { BpCard } from '../BpCard/BpCard';
import { BpTipTap } from '../BpTipTap/BpTipTap';
import { type FileEntryTableType, FileTable, FileTableFieldsDocument } from '../FileTable/FileTable';
import { FileUpload } from '../FileUpload/FileUpload';
import { groupSchema } from './validation/schema';
import styles from './WorkMaterialsForm.module.scss';

const CreateWorkMaterialsMutationDocument = graphql(`
  mutation BpCreateWorkMaterialsInGroupWorkMaterialsForm($input: [WorkMaterialCreateInput!]!) {
    createWorkMaterials(input: $input) {
      workMaterials {
        uuid
        holder {
          ... on TeachingUnit {
            uuid
            group {
              __typename
              uuid
              name
            }
          }
        }
      }
      info {
        nodesCreated
      }
    }
  }
`);
const UpdateWorkMaterialsMutationDocument = graphql(`
  mutation BpUpdateWorkMaterialsinGroupWorkMaterialsForm(
    $update: WorkMaterialUpdateInput!
    $where: WorkMaterialWhere!
  ) {
    updateWorkMaterials(update: $update, where: $where) {
      workMaterials {
        uuid
        __typename
        material {
          uuid
          __typename
          text
          textMediaType
          fileEntries {
            uuid
            __typename
          }
        }
        publicFrom {
          uuid
          __typename
          date
        }
        title
        visibleAfterVisibleTo
        visibleFrom {
          uuid
          __typename
          date
        }
        holder {
          ... on TeachingUnit {
            uuid
            __typename
            group {
              __typename
              uuid
              name
            }
          }
        }
      }
    }
  }
`);

const GroupWorkMaterialsFormFieldsDocument = graphql(`
  fragment GroupWorkMaterialsFormFields on Query {
    workMaterials(where: { uuid: $workMaterialUuid }) {
      uuid
      __typename
      title
      material {
        uuid
        __typename
        text
        fileEntries {
          uuid
          __typename
          ...FileTableFields
        }
      }
      visibleFrom {
        uuid
        __typename
        date
      }
      visibleTo {
        uuid
        __typename
        date
      }
      publicFrom {
        uuid
        __typename
        date
      }
      visibleAfterVisibleTo
    }
  }
`);

type GroupWorkMaterialsFormProps = {
  canArchive: boolean;
  onClose: () => void;
  workMaterialUuid?: string;
  teachingUnitUuid: string;
  query: FragmentType<typeof GroupWorkMaterialsFormFieldsDocument> | undefined;
};

type WorkMaterialValuesType = {
  title: string;
  description: string;
  visibleFrom: string;
  visibleTo: string;
  publicFrom: string;
  visibleAfterVisibleTo: boolean;
  fileEntries: FileEntryTableType[];
  showPublicationDate: boolean;
  teachingUnitUuid: string;
};

export const GroupWorkMaterialsForm: FC<GroupWorkMaterialsFormProps> = ({
  canArchive,
  onClose,
  workMaterialUuid,
  teachingUnitUuid,
  query,
}) => {
  const { t } = useTranslation();
  const { groupUuid } = useParams();
  const { pimAuthClaims } = useAuthClaims();
  const profileUuid = pimAuthClaims.getProfile().uuid;
  const fragment = getFragmentData(GroupWorkMaterialsFormFieldsDocument, query);
  const perms = usePermissionChecker();
  const organizationUuid = useAuthClaims().pimAuthClaims.getOrganizationUuid();

  const { isPhone } = useWindowDimensions();

  const [, bpCreateWorkMaterial] = useMutation(CreateWorkMaterialsMutationDocument);
  const [, bpUpdateWorkMaterial] = useMutation(UpdateWorkMaterialsMutationDocument);

  const workMaterialToEdit = fragment?.workMaterials.find((mat) => mat.uuid === workMaterialUuid);

  const handleSubmit = async (values: WorkMaterialValuesType) => {
    let err: CombinedError | undefined;
    let newWorkmaterial:
      | Pick<
          BpCreateWorkMaterialsInGroupWorkMaterialsFormMutation,
          'createWorkMaterials'
        >['createWorkMaterials']['workMaterials'][0]
      | undefined;
    const { description, title, visibleTo, visibleFrom, publicFrom, visibleAfterVisibleTo } = values;

    const publicFromDate = dayjs(publicFrom).toISOString();
    const visibleFromDate = dayjs(visibleFrom).toISOString();
    const visibleToDate = dayjs(visibleTo).toISOString();

    if (workMaterialToEdit) {
      const { error, data } = await bpUpdateWorkMaterial({
        where: { uuid: workMaterialUuid },
        update: {
          material: {
            delete: {},
            create: {
              node: {
                text: description,
                textMediaType: 'text/plain',
                fileEntries: fileEntriesConnect(values.fileEntries),
              },
            },
          },
          publicFrom: {
            disconnect: {},
            ...connectDay(publicFromDate),
          },
          title: title,
          visibleAfterVisibleTo: visibleAfterVisibleTo,
          visibleFrom: {
            disconnect: {},
            ...connectDay(visibleFromDate),
          },
          visibleTo: {
            disconnect: {},
            ...connectDay(visibleToDate),
          },
          holder: {
            TeachingUnit: {
              disconnect: {},
              ...connectByUuid(teachingUnitUuid),
            },
          },
        },
      });
      newWorkmaterial = data?.updateWorkMaterials.workMaterials[0];
      err = error;
    } else {
      if (
        perms?.canCreateWorkmaterial({
          uuid: groupUuid ?? '',
          organization: { uuid: organizationUuid },
        })
      ) {
        const { error, data } = await bpCreateWorkMaterial({
          input: {
            title: title,
            visibleFrom: connectDay(visibleFromDate),
            visibleTo: connectDay(visibleToDate),
            visibleAfterVisibleTo: visibleAfterVisibleTo,
            publicFrom: connectDay(visibleFromDate),
            owner: connectByUuid(profileUuid),
            material: {
              create: {
                node: {
                  text: description,
                  textMediaType: 'text/plain',
                  fileEntries: fileEntriesConnect(values.fileEntries),
                },
              },
            },
            holder: {
              TeachingUnit: {
                ...connectByUuid(teachingUnitUuid),
              },
            },
          },
        });
        newWorkmaterial = data?.createWorkMaterials.workMaterials[0];
        err = error;
      }
    }

    if (err) showErrorToast(err);
    if (!err) {
      void backendApi.notify({
        type: workMaterialToEdit ? NotificationReason.GroupMaterialUpdated : NotificationReason.GroupMaterialCreated,
        groupUuid: groupUuid,
        groupName: newWorkmaterial?.holder.group.name,
        subjectUuid: workMaterialUuid ?? newWorkmaterial?.uuid ?? '',
        subjectName: values.title,
      });
      showSuccessToast(t('common.saved'));
    }
    onClose();
  };

  const dummyDate = dayjs().toISOString();
  const initialValues: WorkMaterialValuesType = {
    title: workMaterialToEdit?.title ?? '',
    description: workMaterialToEdit?.material.text ?? '',
    visibleFrom: workMaterialToEdit?.visibleFrom?.date ?? dummyDate,
    visibleTo: workMaterialToEdit?.visibleTo?.date ?? dummyDate,
    publicFrom: workMaterialToEdit?.publicFrom?.date ?? dummyDate,
    visibleAfterVisibleTo: workMaterialToEdit?.visibleAfterVisibleTo ?? false,
    showPublicationDate: !!workMaterialToEdit?.publicFrom?.date,
    fileEntries: getFragmentData(FileTableFieldsDocument, workMaterialToEdit?.material.fileEntries ?? []),
    teachingUnitUuid: teachingUnitUuid,
  };

  return (
    <div className={styles['group-workmaterials-form']}>
      <Formik onSubmit={handleSubmit} initialValues={initialValues} validationSchema={groupSchema}>
        {({ resetForm, setFieldValue, setFieldTouched, values, isSubmitting, errors }) => {
          return isSubmitting ? (
            <LazyLoader transparent forceHeight='50vh' />
          ) : (
            <Form className='bp__form'>
              <div className={'bp__form-header'}>
                <InplaceEdit
                  className={styles.title}
                  name='title'
                  size={isPhone ? 'xl' : 'xxxl'}
                  onBlur={async (value) => {
                    await setFieldValue('title', value);
                    await setFieldTouched('title', true);
                  }}
                  onChange={async (value) => {
                    await setFieldValue('title', value);
                    await setFieldTouched('title', true);
                  }}
                  defaultValue={values.title}
                  placeholder={t('common.addTitle')}
                  error={errors.title}
                  dense
                  backgroundColor='background'
                />
                <div className={'bp__form-buttons'}>
                  <Button hierarchy='tertiary' disabled={!canArchive}>
                    {t('common.archive')}
                  </Button>
                  <Button
                    hierarchy='secondary'
                    onClick={() => {
                      resetForm();
                      onClose();
                    }}
                  >
                    {workMaterialUuid ? t('common.discardChanges') : t('common.cancel')}
                  </Button>
                  <Button hierarchy='primary' type={'submit'} disabled={!!errors.title}>
                    {t('common.save')}
                  </Button>
                </div>
              </div>
              <BpCard header={{ headline: t('common.content') }}>
                <BpTipTap
                  className={styles.description}
                  defaultValue={values.description}
                  onChange={async (value) => {
                    await setFieldValue('description', value);
                  }}
                  name={'description'}
                  required={false}
                  placeholder={t('common.addType', {
                    type: t('common.description'),
                  })}
                  error={errors.description}
                />
                {values.fileEntries && values.fileEntries.length > 0 && (
                  <FileTable
                    className={styles.list}
                    mode={'edit'}
                    files={values.fileEntries}
                    onRenamed={async (uuid, newName) => {
                      const updated = values.fileEntries.map((fileEntry) => {
                        if (fileEntry.uuid === uuid) {
                          return { ...fileEntry, filename: newName };
                        }
                        return fileEntry;
                      });
                      await setFieldValue('fileEntries', updated);
                    }}
                    onDeleted={async (uuid) => {
                      await setFieldValue(
                        'fileEntries',
                        values.fileEntries.filter((fileEntry) => {
                          return fileEntry.uuid !== uuid;
                        }),
                      );
                    }}
                    isGroupEditor
                    fullHeight
                  />
                )}
                <FileUpload
                  onFileUpload={async (file) => {
                    await setFieldValue('fileEntries', [...values.fileEntries, file]);
                  }}
                />
              </BpCard>
            </Form>
          );
        }}
      </Formik>
    </div>
  );
};
